import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import BootstrapTable from 'react-bootstrap-table-next';
import Toolbar from '../../common/Toolbar';
import Switch from '../../common/Switch';
import styles from './DropshipProductCardVariants.module.scss';
import { currencyFormat, stringOrDashFormatter } from '../../../utils/formatHelper';
import { variantSort2 } from '../../../utils/sortHelper';
import { sortCaret, getDynamicColumn } from '../../../utils/tableHelper';
import { partition, upperFirst } from 'lodash';
import { useFeatureFlag } from '../../../hooks/useFeatureFlag';
import { showPricesRetailerSide } from '../../../constants/featureFlag';
import { useRetailerType } from '../../../hooks/useRetailerType';
import { UNIT_PRICE_COLUMN, WHOLESALE_PRICE_KEY, RETAIL_PRICE_KEY } from '../../../constants/pricings';
import { NO_VALUE_PLACEHOLDER } from '../../../constants/tables';
import { useDispatch, useSelector } from 'react-redux';
import {
  dropshipProductsApprove,
  dropshipProductsSaveVariants,
  dropshipProductsUnapprove,
} from '../../../store/actions/dropshipProducts';
import { ensureArray } from '../../../utils/array';
function dataFormatColumn(cell, row, rowIndex, field) {
  return stringOrDashFormatter(row.options[field]);
}

function DropshipProductCardVariants(props) {
  const dispatch = useDispatch();

  const { variants = [] } = props;

  useEffect(
    function initializeApprovedVariantsInRedux() {
      const [approvedVariants, unapprovedVariants] = partition(
        variants,
        (variant) => variant.ecommerce_approved_by_retailer,
      );

      dispatch(dropshipProductsApprove(approvedVariants.map(({ id }) => id)));
      dispatch(dropshipProductsUnapprove(unapprovedVariants.map(({ id }) => id)));
    },
    [dispatch, variants],
  );

  const { approved: approvedVariantIds } = useSelector((state) => state.dropshipProducts);

  const isShowPricesRetailerSideEnabled = useFeatureFlag(showPricesRetailerSide, false);

  const optionsColumns = getDynamicColumn(variants);

  const approveOrRevokeVariant = (shouldApprove, variantIdOrIds) => {
    const variantIds = ensureArray(variantIdOrIds);

    if (shouldApprove) {
      dispatch(dropshipProductsApprove(variantIds));
    } else {
      dispatch(dropshipProductsUnapprove(variantIds));
    }

    dispatch(dropshipProductsSaveVariants());
  };

  const approveOrRevokeAllVariants = (shouldApprove) => {
    if (!variants?.length) return;

    const variantsIds = variants.map(({ id }) => id);
    approveOrRevokeVariant(shouldApprove, variantsIds);
  };

  const isEveryVariantApproved = () => variants.every((variant) => approvedVariantIds.includes(variant.id));

  const approveOrRevokeVariantSwitch = (cell, row, rowIndex, { approveOrRevokeVariant }) => {
    return (
      <Switch
        checked={approvedVariantIds.includes(row.id)}
        onSwitch={(shouldApprove) => approveOrRevokeVariant(shouldApprove, row.id)}
      />
    );
  };

  const columns = [];

  columns.push({
    text: 'Approve',
    dataField: 'id',
    formatter: approveOrRevokeVariantSwitch,
    formatExtraData: { approveOrRevokeVariant },
    headerFormatter() {
      return <Switch checked={isEveryVariantApproved()} onSwitch={approveOrRevokeAllVariants} />;
    },
    isDummyField: true,
    classes: 'narrow',
    headerClasses: 'narrow',
  });

  optionsColumns.forEach((field) => {
    columns.push({
      text: upperFirst(field),
      dataField: field,
      sort: true,
      formatter: dataFormatColumn,
      formatExtraData: field,
    });
  });

  const { priceType } = useRetailerType();

  columns.push(
    {
      text: 'SKU',
      dataField: 'sku',
      sort: true,
    },
    {
      text: 'Units',
      dataField: 'available_inventory',
      formatter: (cell) => `${cell} Units In Cymbio`,
    },
    {
      text: 'Price',
      dataField: 'priceData',
      formatter: (cell, row) => {
        /*
         * the unit price key gets transformed to wholesale_price in the backend
         * see lib/variant/variant.aggregator.helper.ts,
         * from this PR: https://github.com/Cymbio/cymbio-api-3/pull/7023
         */
        const priceKey = priceType === UNIT_PRICE_COLUMN ? WHOLESALE_PRICE_KEY : RETAIL_PRICE_KEY;
        const priceData = row[priceKey];

        return priceData ? currencyFormat(priceData, row.currency) : NO_VALUE_PLACEHOLDER;
      },
    },
    {
      text: 'MSRP',
      dataField: RETAIL_PRICE_KEY,
      formatter: (cell, row) => currencyFormat(cell, row.currency),
    },
  );

  const uiColumns = columns.filter((column) => {
    if (column.dataField === RETAIL_PRICE_KEY) {
      return isShowPricesRetailerSideEnabled;
    }

    return true;
  });

  return (
    <div className={styles.container}>
      <Toolbar name="Variants" />

      <BootstrapTable
        data={variants}
        columns={uiColumns}
        keyField="id"
        striped
        hover
        bordered={false}
        className={styles.container}
        sort={{
          sortCaret,
          sortFunc: variantSort2,
        }}
        defaultSorted={[
          {
            dataField: optionsColumns.length ? optionsColumns[0] : 'sku',
            order: 'desc',
          },
        ]}
      ></BootstrapTable>
    </div>
  );
}

export default DropshipProductCardVariants;

DropshipProductCardVariants.propTypes = {
  variants: PropTypes.arrayOf(PropTypes.object).isRequired,
};
